// Subscription plan catalogue. Each plan exposes the raw limits via the
// `limits` field so the backend can enforce quotas, while the derived fields
// (`charLimit`, `projectLimit`, …) keep backwards compatibility with the
// existing UI.

const UNLIMITED = Number.MAX_SAFE_INTEGER;
const GB = 1024 * 1024 * 1024;

const createPlan = ({ label, kind, limits, support, aiTools, price, features, stripePriceId }) => {
    // Seat axis is split into managers vs linguists. We accept either explicit
    // managerSeats/linguistSeats OR the legacy single `teamMembers` value (then
    // treated as the manager-seat allowance with no separate linguist budget).
    const managerSeats = limits.managerSeats ?? limits.teamMembers ?? UNLIMITED;
    const linguistSeats = limits.linguistSeats ?? 0;

    // Word allowances (unit = words). `aiWords` is the engine-usage sub-cap
    // (DeepL/Google/OpenAI); `translatedWords` is the overall workspace cap
    // (human + TM + AI). `characters` is kept as a deprecated alias of aiWords
    // so legacy readers (frontend, controllers mid-migration) keep working.
    const aiWords = limits.aiWords ?? limits.characters ?? UNLIMITED;
    const translatedWords = limits.translatedWords ?? UNLIMITED;

    const normalizedLimits = {
        aiWords,
        translatedWords,
        characters: aiWords,
        projects: limits.projects ?? UNLIMITED,
        managerSeats,
        linguistSeats,
        // Legacy alias — equal to managerSeats. New code should read
        // managerSeats / linguistSeats directly; teamMembers/teamLimit remain
        // so existing callers keep working until they're migrated.
        teamMembers: managerSeats,
        targetLanguages: limits.targetLanguages ?? UNLIMITED,
        storageBytes: limits.storageBytes ?? UNLIMITED,
        apps: limits.apps ?? UNLIMITED,
        ocrCredits: limits.ocrCredits ?? 0,
    };

    return {
        label,
        kind: kind ?? 'workspace',
        limits: normalizedLimits,
        support,
        aiTools: aiTools ?? false,
        price: price ?? 'Contact Us',
        features: features ?? {},
        stripePriceId: stripePriceId ?? null,
        // Backwards compatible aliases used across the application today
        charLimit: normalizedLimits.characters,
        projectLimit: normalizedLimits.projects,
        teamLimit: normalizedLimits.managerSeats,
        managerSeatLimit: normalizedLimits.managerSeats,
        linguistSeatLimit: normalizedLimits.linguistSeats,
        maxTargetLanguages: normalizedLimits.targetLanguages,
        storageLimit: normalizedLimits.storageBytes,
    };
};

const ACCOUNT_PLANS = {
    free: createPlan({
        label: 'Free',
        limits: {
            translatedWords: 5_000,
            aiWords: 0,
            projects: 5,
            managerSeats: 0,
            linguistSeats: 0,
            targetLanguages: 5,
            storageBytes: 500 * 1024 * 1024,
            apps: 0,
            ocrCredits: 0,
        },
        support: 'email',
        aiTools: true,
        price: '£0',
        features: {
            fuzzyMatching: false,
            tmxExport: false,
            bulkGlossary: false,
            complexFormats: false,
            batchProcessing: false,
            externalSharing: false,
            teamAccess: false,
            chatAccess: false,
            analytics: false,
            // Layout Risk: the pre-flight summary (risk counts) is free for all
            // plans. Per-frame detail + Auto-fit are free for DOCX, but gated on
            // this flag (Growth+) for IDML — see detailUnlockedFor in
            // routes/layoutRisk.js. (IDML upload already requires Growth.)
            layoutRisk: false,
            // Brand Voice is gated to Growth+ — its headline value (AI applying
            // the profile to drafts, live preview) runs on premiumAI, which is
            // itself Growth+. Lower tiers see an upgrade teaser. Enforced on the
            // billing user's plan so seated team members inherit workspace access.
            brandVoice: false,
            // Engine gating — Free has NO machine/AI translation so it can never
            // spend DeepL/Google/OpenAI credits. Free users translate manually
            // or from TM only, bounded by the general words allowance.
            machineTranslation: false,
            premiumAI: false,
        },
    }),
    essential: createPlan({
        label: 'Essential',
        limits: {
            translatedWords: 50_000,
            aiWords: 10_000,
            projects: 20,
            managerSeats: 1,
            linguistSeats: 3,
            targetLanguages: 25,
            storageBytes: 5 * GB,
            apps: 1,
            ocrCredits: 50,
        },
        support: 'email',
        aiTools: true,
        price: '£79',
        stripePriceId: process.env.STRIPE_PRICE_ID_ESSENTIAL || 'price_1T19DaH3EGdU5Kf3DDU5Xd8x',
        features: {
            fuzzyMatching: false,
            tmxExport: false,
            bulkGlossary: false,
            complexFormats: false,
            batchProcessing: false,
            externalSharing: false,
            teamAccess: true,
            chatAccess: false,
            analytics: false,
            layoutRisk: false,
            // Brand Voice gated to Growth+ (see free plan note).
            brandVoice: false,
            // Standard MT (DeepL/Google) included; premium AI (OpenAI/LLM)
            // gated to Growth+ so the most expensive credits aren't reachable
            // on the cheapest paid tier.
            machineTranslation: true,
            premiumAI: false,
        },
    }),
    growth: createPlan({
        label: 'Growth',
        limits: {
            translatedWords: 250_000,
            aiWords: 100_000,
            projects: 200,
            managerSeats: 5,
            linguistSeats: 25,
            targetLanguages: 100,
            storageBytes: 50 * GB,
            apps: 5,
            ocrCredits: 500,
        },
        support: 'premium',
        aiTools: true,
        price: '£249',
        stripePriceId: process.env.STRIPE_PRICE_ID_GROWTH,
        features: {
            fuzzyMatching: true,
            tmxExport: true,
            bulkGlossary: true,
            complexFormats: true,
            batchProcessing: true,
            externalSharing: true,
            teamAccess: true,
            chatAccess: true,
            analytics: true,
            layoutRisk: true,
            brandVoice: true,
            machineTranslation: true,
            premiumAI: true,
            // Autopilot dry-run (proposes only — scores, would-approve, would-fix,
            // would-escalate) is available here. Live auto-apply is Scale-only
            // via the `autopilotAutoApply` flag below.
            autopilot: true,
        },
    }),
    scale: createPlan({
        label: 'Scale',
        limits: {
            translatedWords: 1_000_000,
            aiWords: 500_000,
            projects: UNLIMITED,
            managerSeats: UNLIMITED,
            linguistSeats: UNLIMITED,
            targetLanguages: UNLIMITED,
            storageBytes: UNLIMITED,
            apps: UNLIMITED,
            ocrCredits: 10_000,
        },
        support: 'dedicated',
        aiTools: true,
        price: '£599',
        stripePriceId: process.env.STRIPE_PRICE_ID_SCALE,
        features: {
            fuzzyMatching: true,
            tmxExport: true,
            bulkGlossary: true,
            complexFormats: true,
            batchProcessing: true,
            externalSharing: true,
            teamAccess: true,
            chatAccess: true,
            analytics: true,
            sso: true,
            apiAccess: true,
            auditLogs: true,
            layoutRisk: true,
            brandVoice: true,
            machineTranslation: true,
            premiumAI: true,
            // Scale-exclusive: synthetic reader-panel reaction prediction.
            resonance: true,
            // AI Quality that runs itself. Dry-run is available from Growth; the
            // live auto-apply / hand-over-the-keys step is Scale-exclusive.
            autopilot: true,
            autopilotAutoApply: true,
        },
    }),
    // Phase A: scoped linguist account. These users join one or more workspaces
    // as a linguist seat but do not get their own billing/projects-own scope.
    // Limits are deliberately zeroed; their effective capabilities come from
    // the workspace(s) that seat them.
    linguist: createPlan({
        label: 'Linguist',
        kind: 'linguist',
        limits: {
            translatedWords: 0,
            aiWords: 0,
            projects: 0,
            managerSeats: 0,
            linguistSeats: 0,
            targetLanguages: UNLIMITED,
            storageBytes: 0,
            apps: 0,
            ocrCredits: 0,
        },
        support: 'email',
        aiTools: false,
        price: '£0',
        features: {
            ownWorkspace: false,
            billingVisible: false,
            canBeSeated: true,
            teamAccess: true,
            // Linguists act inside a workspace; engine availability comes from
            // the owner's plan, not their own. Zeroed here for direct-call safety.
            // Brand Voice access is resolved via the workspace billing user's
            // plan (billingScope gate), so this self-flag stays false.
            brandVoice: false,
            machineTranslation: false,
            premiumAI: false,
        },
    }),
};

module.exports = ACCOUNT_PLANS;
